
import cs from 'classnames';
import React, { useState } from 'react';
import { useAppContext } from '../../app/AppContext';
import { PhoneNumberLink } from 'shared/components/PhoneNumber';
import { TextWithTermsLinks } from '../../components/TextWithTermsLinks';
import { ApplicationResidentStatus, ImportantInfoFragment, useSetResidentStatusMutation, CartItemFragment } from '../../generated.graphql';
import { useElementId } from 'shared/utils/elementIds';
import { Section } from './ImportantInfoSection';
import { ProductTypeInfo } from './ProductTypeInfo';
import { BorderedSpan } from '../cart/MinMonthsSelect';

interface Props extends Omit<ImportantInfoFragment, '__typename'> {
  residentStatus: ApplicationResidentStatus | null
  residentStatusChoices: {
    value: string
    name: string
  }[]
  portalSlug: string
  items: readonly CartItemFragment[]
}


const ContentFreight = (props: Props) => (
  <div dangerouslySetInnerHTML={{__html: props.freightHtml}}/>
);

const ContentRentalProcess = (props: Props) => (
  <div>
    <div dangerouslySetInnerHTML={{__html: props.rentalProcessHtml}}/>
    {props.productTypeInfo.length > 0 && 
      <p>Click below for more information on:</p>
    }
    {props.productTypeInfo.map(lnk => (
      <ProductTypeInfo key={lnk.title} {...lnk}/>
    ))}
  </div>
);

const ContentApplicantAge = (props: Props & {isInvalid: boolean}) => {
  const elName = useElementId('ResidentStatus');
  const [, mutate] = useSetResidentStatusMutation();
  const [disabled, setDisabled] = useState(false);
  const app = useAppContext();

  return (
    <div>
      <div className="mb-3" dangerouslySetInnerHTML={{__html: props.ageAndResidencyHtml}}/>
      {props.residentStatusChoices.map((c, idx) => (
        <div className={cs("custom-control custom-radio mt-1")}>
          <input 
            type="radio" 
            value={c.value} 
            checked={c.value === props.residentStatus} 
            id={`${elName}-${c.value}`} 
            name={elName} 
            disabled={disabled}
            className={cs("custom-control-input", props.isInvalid && "is-invalid")}
            onChange={async e => {
              setDisabled(true);
              await mutate({
                input: {
                  portalSlug: app.portalSlug, 
                  residentStatus: e.target.value,
                },
              });
              setDisabled(false);
            }}
          />
          <label className="custom-control-label" htmlFor={`${elName}-${c.value}`}>{c.name}</label>
          {idx === props.residentStatusChoices.length - 1 && (
            <div className="invalid-feedback">
              You must provide residency status before continuing
            </div>
          )}
        </div>
      ))}
      {props.residentStatus === 'V' && (
        <div className="alert alert-warning mt-2 mb-0">
          First time renters who currently hold a visa are limited to a maximum rental amount of $160 per month. If you would like to discuss this before completing your application, please feel free to give our customer service team a call on <PhoneNumberLink />.
        </div>
      )}
    </div>
  )
}

const ContentMinRentalPeriod = (props: Props) => (
  <div>
    <TextWithTermsLinks text={props.minPeriodText} portalSlug={props.portalSlug}/>
    <ul className="mt-3">
      {props.items.map(item => (
        <li>{item.title} <br className='d-block d-sm-none'/><span className='d-none d-sm-inline'>- </span><b><BorderedSpan>{item.selectedRate?.minMonths ?? item.minMonths} month</BorderedSpan> minimum</b> rental period</li>
      ))}
    </ul>
  </div>
);

const ContentNamedApplicants = (props: Props) => (
  <TextWithTermsLinks text={props.namedApplicantsText} portalSlug={props.portalSlug}/>
);

export const useSections = (props: Props): Section[] => {
  // NB. The sections shown used to be variable based on the application, but no longer is.  
  //     But keeping the framework for this here just in case.
  
  const residentStatusIsInvalid = !props.residentStatus;

  const [touchedState, setTouchedState] = useState(new Array(5).fill(false));
  const setTouched = (index: number, touched: boolean = true) => {
    if ((touchedState?.[index] ?? touched) === touched) return;
    const newTouched = [...touchedState];
    newTouched[index] = touched;
    setTouchedState(newTouched);
  }
  
  return [
    {
      title: "How the rental process works",
      children: <ContentRentalProcess {...props}/>,
    },
    {
      title: props.freightHeading, 
      children: <ContentFreight {...props}/>,
    },
    {
      title: "Applicants must be 18+",
      children: <ContentApplicantAge {...props} isInvalid={touchedState[2] && residentStatusIsInvalid}/>,
      isValid: () => !residentStatusIsInvalid,
      setTouched: (touched) => { setTouched(2, touched); },
    },
    {
      title: 'Minimum rental period',
      children: <ContentMinRentalPeriod {...props}/>,
    },
    {
      title: 'Named applicants only',
      children: <ContentNamedApplicants {...props}/>,
    },
  ]
};
